/* eslint-disable  import/no-extraneous-dependencies, react/no-array-index-key */
import { Divider, Grid } from '@mui/material';
import { ActionButton, AddItemButton, BasicButton } from '@tassat/button';
import {
    SelectField,
    SimpleErrorMessage,
    UserRoleField
} from '@tassat/formik-input';
import { Delete } from '@tassat/icon';
import { ClientWalletRolesMap, SelectOption } from '@tassat/ui-definitions';
import { FieldArray, Formik, FormikProps } from 'formik';
import * as React from 'react';

import { validateUserWalletMappingForm } from './submission';
import { IFormProps, IFormValues, IFormikProps } from './types';

const FormContents = (props: IFormProps) => {
    const {
        classes,
        accounts,
        values,
        errors,
        features,
        submitForm,
        isSubmitting,
        submitError,
        isValid
    } = props;

    const [ accountOptions, setAccountOptions ] = React.useState<SelectOption[]>([]);
    const unmappedAccounts = accounts.length;

    const getAllowedRoles = (accessControl) => {
        const allowedRoles = accessControl ?
            ClientWalletRolesMap.filter((role) => role.label !== 'F')
            : ClientWalletRolesMap.filter((role) => role.label !== 'M' && role.label !== 'A');

        const mappedAllowedRoles = allowedRoles.map((data) => ({
            value: data.id,
            label: data.label
        }));

        return mappedAllowedRoles;
    };

    const walletOptionRenderer = props => {
        const { innerProps, innerRef } = props;
        const account = props.data.refData.account;

        return (
            <div
                ref={innerRef}
                {...innerProps}
                className={classes.walletOptionSelectWrapper}
            >
                <div className={classes.walletOptionSelect}>
                    <div className={`${classes.walletOptionSelect}__left`}>
                        <div>{account.AccountName}</div>
                    </div>
                </div>
            </div>
        );
    };

    const walletSingleSelectValueRenderer = props => {
        const { innerProps, innerRef } = props;
        const account = props.data.refData.account;

        return (
            <div
                ref={innerRef}
                {...innerProps}
                className={classes.walletValueSelect}
            >
                <div className={`${classes.walletValueSelect}__left`}>
                    <div>{account.AccountName}</div>
                </div>
            </div>
        );
    };

    React.useEffect(() => {
        setAccountOptions(
            accounts
                .filter((accObj) => {
                    let notMapped = true;
                    const { account } = accObj;
                    values.Mappings.forEach((mapping) => {
                        if (mapping.Wallet && account.AccountId === mapping.Wallet.value) {
                            notMapped = false;
                        }
                    });

                    return notMapped;
                })
                .map((accObj) => {
                    const { account } = accObj;
                    const walletName = account && account.AccountName ? account.AccountName : '';

                    return {
                        label: `${walletName}`,
                        value: account.AccountId,
                        refData: accObj
                    };
                })
        );
    }, [JSON.stringify(values.Mappings)]);

    return (
        <form className={classes.flexColumn}>
            <Grid
                container
                direction='column'
                spacing={0}
            >
                <FieldArray
                    name='Mappings'
                    render={(arrayHelpers) => (
                        <>
                            {values.Mappings.map((mapping, index) => (
                                <Grid
                                    container
                                    direction='column'
                                    key={`mapping_${index}`}
                                    wrap='nowrap'
                                >
                                    <Grid
                                        className={classes.fullWidth}
                                        container
                                        item
                                        spacing={3}
                                        wrap='nowrap'
                                        xs={12}
                                    >
                                        <Grid
                                            className={classes.walletSelectWrapper}
                                            container
                                            justifyContent='space-between'
                                        >
                                            <Grid
                                                className={classes.walletSelect}
                                                item
                                                xs='auto'
                                            >
                                                <SelectField
                                                    autoComplete
                                                    errors={errors}
                                                    key={`wallet_select_${index}`}
                                                    label='Select Account'
                                                    name={`Mappings.${index}.Wallet`}
                                                    nestedValue
                                                    OptionRenderer={undefined}
                                                    options={accountOptions}
                                                    SingleSelectValueRenderer={undefined}
                                                    values={values}
                                                />
                                            </Grid>
                                            {values.Mappings[index].Wallet &&
                                                (
                                                <Grid
                                                    className={classes.roles}
                                                    item
                                                >
                                                    <UserRoleField
                                                        allowedRoles={
                                                            getAllowedRoles(
                                                                values.Mappings[index]
                                                                    .Wallet?.refData
                                                                    .account
                                                                    .AccessControl
                                                            )
                                                        }
                                                        errors={errors}
                                                        key={`role_select_${index}`}
                                                        label='Role'
                                                        name={`Mappings.${index}.Role`}
                                                    />
                                                </Grid>
                                            )}
                                        </Grid>
                                        {values.Mappings.length > 1 &&
                                            (
                                            <Grid
                                                className={classes.removeMappingContainer}
                                                item
                                                xs={1}
                                            >
                                                <ActionButton
                                                    handleClick={() => arrayHelpers.remove(index)}
                                                    Icon={Delete}
                                                    key={`remove_mapping_${index}`}
                                                    text='Delete'
                                                    type='icon'
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                    {values.Mappings.length > 1 &&
                                        index !== values.Mappings.length - 1 &&
                                        (
                                        <Divider
                                            className={classes.fieldMappingDivider}
                                            orientation='horizontal'
                                            variant='fullWidth'
                                        />
                                    )}
                                </Grid>
                            ))}
                            {accountOptions.length > 0 && values.Mappings.length < unmappedAccounts &&
                                (
                                <Grid
                                    className={classes.newMappingContainer}
                                    container
                                    item
                                    justifyContent='flex-start'
                                    spacing={2}
                                    xs={12}
                                >
                                    <Grid item>
                                        <AddItemButton
                                            onClick={() => arrayHelpers.push({
                                                Wallet: null,
                                                Role: ''
                                            })}
                                            text='Add New Mapping'
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </>
                    )}
                />
                <Grid
                    container
                    item
                    justifyContent='center'
                    xs={12}
                >
                    <BasicButton
                        className={classes.submitButton}
                        disabled={isSubmitting || !isValid}
                        onClick={submitForm}
                        text='Submit'
                        type='submit'
                    />
                    <SimpleErrorMessage
                        className={classes.submitError}
                        error={submitError}
                    />
                </Grid>
            </Grid>
        </form>
    );
};

export function UserWalletMappingForm(props: IFormikProps) {
    const {
        classes,
        onClose,
        formProps
    } = props;

    const {
        accounts,
        user,
        features,
        submitUserWalletMappingForm
    } = formProps;

    const [ submitError, setSubmitError ] = React.useState<string>('');

    const initialValues: IFormValues = {
        Mappings: [
            {
                Wallet: null,
                Role: ''
            }
        ]
    };

    const userAccountMappingOriginalValidation = (values: IFormValues) => validateUserWalletMappingForm(values);

    return (
        <Grid
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                item
                xs={12}
            >
                <Formik
                    initialValues={initialValues}
                    onSubmit={async (values: IFormValues) => {
                        await submitUserWalletMappingForm(values, {
                            userId: user.UserId,
                            setSubmitError,
                            onClose
                        });
                    }}
                    validate={(values: IFormValues) => (userAccountMappingOriginalValidation(values))}
                    validateOnChange
                    validateOnMount
                >
                    {(formContentsProps: FormikProps<IFormValues>) => (
                        <FormContents
                            {...formContentsProps}
                            accounts={accounts}
                            classes={classes}
                            features={features}
                            formProps={formProps}
                            submitError={submitError}
                        />
                    )}
                </Formik>
            </Grid>
        </Grid>
    );
}